import React, { useContext, useEffect, useState } from "react";
import { db, storage } from "../firebase";
import axios from "axios";
import ReactPlayer from "react-player";
import "./PostList.scss";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import CreateComment from "./CreateComment";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { MemberContext } from "../Contexts/MemberContext";
import { Comments } from "./Comments";
import { PostEditForm } from "./PostEditForm";
import { useLocation, useNavigate } from "react-router-dom";
import { backGroundImage } from "../utils/backGroundImage";

const PostList = () => {
  const [displayImages, setDisplayImages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [videoTitles, setVideoTitles] = useState({});
  const [editingPosts, setEditingPosts] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { currentUserInfo, membersLoading, currentUser } =
    useContext(MemberContext);

  const [selected, setSelected] = useState("event_report");
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedSection, setSelectedSection] = useState(false);
  const { createsection, trueSectionsString } = location.state || {};

  const [shouldHidePlayer, setShouldHidePlayer] = useState(false);

  // ------------------------------------------------------------
  // 🔥 スマホ対策：YouTube の埋め込み条件判定
  // ------------------------------------------------------------
  useEffect(() => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isSmallScreen = window.innerWidth <= 1500;
    setShouldHidePlayer(isIOS || isSmallScreen);

    const handleResize = () => {
      const isNowSmall = window.innerWidth <= 500;
      setShouldHidePlayer(isIOS || isNowSmall);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ------------------------------------------------------------
  // 🔥 ページアクセス権のチェック（Android・iPhone 遅延対策）
  // ------------------------------------------------------------
  useEffect(() => {
    if (membersLoading) return;

    // ✅ PostList は「ログインしているか」だけを見る
    if (!currentUser) {
      alert("ログインしてください。");
      navigate("/eventlist");
    }
  }, [currentUser, membersLoading, navigate]);

  // ------------------------------------------------------------
  // 🔥 Firestore 投稿取得
  // ------------------------------------------------------------
  useEffect(() => {
    const q = query(collection(db, "posts"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPosts(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  // ------------------------------------------------------------
  // 🔥 初期セクション選択
  // ------------------------------------------------------------
  useEffect(() => {
    let initial = trueSectionsString || createsection || "event_report";
    setSelected(initial);
  }, [trueSectionsString, createsection]);

  // ------------------------------------------------------------
  // 🔥 セクション別フィルタリング
  // ------------------------------------------------------------
  useEffect(() => {
    if (selectedSection) {
      setFilteredPosts(posts);
      return;
    }

    const filterKey = selected;
    setFilteredPosts(
      posts.filter((post) => post.section?.[filterKey] === true)
    );
  }, [posts, selected, selectedSection]);

  // ------------------------------------------------------------
  // 🔥 YouTubeタイトル取得
  // ------------------------------------------------------------
  useEffect(() => {
    posts.forEach((post) => {
      if (post.youtubeUrl) {
        fetchYoutubeTitle(post.youtubeUrl);
      }
    });
  }, [posts]);

  const fetchYoutubeTitle = async (url) => {
    const videoId = extractVideoId(url);
    if (!videoId) return;

    try {
      const response = await axios.get(
        `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=AIzaSyCRnrK-yoLvx75Kzqlqj_M6j-apwoo8-3s`
      );
      const title = response.data.items[0]?.snippet?.title;
      if (title) {
        setVideoTitles((prev) => ({ ...prev, [videoId]: title }));
      }
    } catch (error) {
      console.error("YouTube API のエラー:", error);
    }
  };

  const extractVideoId = (url) => {
    const regex = /(?:v=|\/)([0-9A-Za-z_-]{11}).*/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // ------------------------------------------------------------
  // 🔥 背景画像
  // ------------------------------------------------------------
  useEffect(() => {
    backGroundImage(setDisplayImages);
  }, []);

  const displayImage = displayImages.length > 0 ? displayImages[0].src : "";

  // ------------------------------------------------------------
  // 🔥 UIヘルパー
  // ------------------------------------------------------------
  const formatTimestamp = (timestamp) => {
    const date = timestamp.toDate();
    return date.toLocaleString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const Link = (str) => {
    const regexp_url = /https?:\/\/[\w!#$%&'()*+,.\/:;=@?~\[\]-]+/g;
    return str.replace(
      regexp_url,
      (url) =>
        `<div><a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a></div>`
    );
  };

  const toggleEdit = (postId) => {
    setEditingPosts((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  // ------------------------------------------------------------
  // 🔥 投稿削除
  // ------------------------------------------------------------
  const handleDeletePost = async (postId) => {
    const target = posts.find((p) => p.id === postId);
    if (!target) return;

    const ok = window.confirm(`本当に「${target.title}」を削除しますか？`);
    if (!ok) return;

    // 画像がある場合は削除
    if (target.imageUrl) {
      try {
        const imagePath = decodeURIComponent(target.imageUrl.split("?")[0]);
        const storagePath = imagePath
          .split("o/")[1]
          .replace(/^blog-images\//, "");
        const storageRef = ref(storage, `blog-images/${storagePath}`);
        await deleteObject(storageRef);
      } catch (error) {
        console.error("画像削除エラー:", error);
      }
    }

    // 投稿削除
    try {
      await deleteDoc(doc(db, "posts", postId));
      alert("投稿を削除しました。");
    } catch (error) {
      console.error("投稿削除エラー:", error);
    }
  };

  // ------------------------------------------------------------
  // 🔥 投稿編集
  // ------------------------------------------------------------
  const handleEditPost = async (
    postId,
    newTitle,
    newSection,
    newContent,
    newParticipant,
    newImageUrl,
    newYoutubeUrl
  ) => {
    setLoading(true);

    const postDocRef = doc(db, "posts", postId);
    const postDocSnap = await getDoc(postDocRef);
    let currentImageUrl = postDocSnap.data()?.imageUrl;

    // 既存画像削除
    if (currentImageUrl) {
      try {
        const storageRef = ref(storage, currentImageUrl);
        await deleteObject(storageRef);
      } catch (error) {
        console.error("画像削除エラー:", error);
      }
    }

    // 新画像アップロード
    let imageUrl = newImageUrl;
    if (imageUrl instanceof Blob) {
      const storageRef = ref(storage, `images/${postId}`);
      await uploadBytes(storageRef, imageUrl);
      imageUrl = await getDownloadURL(storageRef);
    }

    // 更新
    await updateDoc(postDocRef, {
      title: newTitle,
      section: newSection,
      content: newContent,
      participant: newParticipant ?? "",
      imageUrl: imageUrl ?? "",
      youtubeUrl: newYoutubeUrl ?? "",
      updatedAt: serverTimestamp(),
    });

    setEditingPosts(false);
    setLoading(false);
  };

  // ------------------------------------------------------------
  // 🔥 レンダリング
  // ------------------------------------------------------------
  if (membersLoading || !currentUser || !currentUserInfo) {
    return (
      <div className="postlist_loading_container">
        <div className="postlist_loading">読み込み中...</div>
      </div>
    );
  }

  const options = [
    { jp: "イベント報告", en: "event_report" },
    { jp: "会の情報", en: "member_exchange" },
    { jp: "テニス情報", en: "recommendations" },
    { jp: "よもやま話", en: "others" },
  ];

  return (
    <>
      <div
        className="postlist_container"
        style={{ backgroundImage: `url(${displayImage})` }}
      >
        <div className="postlist_content1">
          <div className={`postlist_field0 postlist_color_${selected}`}>
            <div className="postlist_title_select">
              <div className="postlist_title_select_label">
                {options.map(({ en, jp }) => (
                  <label
                    key={en}
                    className={`checkbox-label_${selected === en ? en : ""}`}
                    onClick={() => setSelected(en)}
                  >
                    {jp}
                  </label>
                ))}

                <div className="postlist_home">
                  <span
                    className="postlist_post_home"
                    onClick={() => navigate(`/`)}
                  >
                    return
                    <br />
                    home
                  </span>
                </div>
              </div>

              <div className="postlist_post">
                <span
                  className="postlist_post_title"
                  onClick={() => navigate(`/createpost`)}
                >
                  投稿
                </span>
              </div>
            </div>
          </div>

          <div className="postlist_content">
            {loading && <div className="postlist_loading">Loading...</div>}

            {filteredPosts.map((post) => (
              <div
                key={post.id}
                className={`postlist_field postlist_color_${
                  post.section.event_report
                    ? "event_report"
                    : post.section.member_exchange
                    ? "member_exchange"
                    : post.section.recommendations
                    ? "recommendations"
                    : post.section.others
                    ? "others"
                    : ""
                }`}
              >
                <div className="postlist_field_inner_title">
                  <h3>{post.title}</h3>

                  {/* 編集 UI */}
                  {!editingPosts[post.id] && (
                    <div className="postlist_postusername">
                      <p>
                        {post.postaccountname}
                        <br />
                        {!post.participant && formatTimestamp(post.createdAt)}
                      </p>

                      {currentUserInfo?.administrator && (
                        <div
                          className="toggle-edit-button"
                          onClick={() => toggleEdit(post.id)}
                        >
                          ︙
                        </div>
                      )}
                    </div>
                  )}

                  {/* 編集フォーム */}
                  {editingPosts[post.id] && (
                    <div className="postlist_field_inner_title_button">
                      <PostEditForm
                        postId={post.id}
                        existingTitle={post.title}
                        existingSection={post.section}
                        existingContent={post.content}
                        existingParticipant={post.participant}
                        existingYoutubeUrl={post.youtubeUrl}
                        existingNewImage={post.imageUrl}
                        handleEditPost={handleEditPost}
                        setEditingPosts={setEditingPosts}
                      />

                      <button
                        className="postlist_field_content_cancel2_button"
                        onClick={() => setEditingPosts(false)}
                      >
                        キャンセル
                      </button>
                      <button
                        className="postlist_field_content_delete_button"
                        onClick={() => handleDeletePost(post.id)}
                      >
                        削除
                      </button>
                    </div>
                  )}
                </div>

                {/* 本文表示 */}
                <div className="postlist_field_content">
                  {post.imageUrl && (
                    <img
                      className="postlist_field_content_image"
                      src={post.imageUrl}
                      alt={post.title}
                    />
                  )}

                  <div className="postlist_field_content_youtube">
                    {!shouldHidePlayer && (
                      <div className="youtube-embed">
                        <ReactPlayer
                          key={`${post.youtubeUrl}-${location.pathname}`}
                          url={post.youtubeUrl}
                          width="100%"
                          height="315"
                          controls
                          playsinline
                        />
                      </div>
                    )}

                    <div className="youtube-link">
                      <a
                        href={post.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        ▶ YouTubeで見る
                      </a>
                    </div>
                  </div>

                  <div className="postlist_field_content_text">
                    <p
                      className="postlist_field_content_text_text"
                      dangerouslySetInnerHTML={{ __html: Link(post.content) }}
                    ></p>

                    {post.participant && (
                      <p>
                        <span className="postlist_field_content_text_text_small">
                          参加者({post.participant})
                        </span>
                      </p>
                    )}

                    <div className="postlist_field_content_comment">
                      <Comments postId={post.id} />
                      <CreateComment
                        postId={post.id}
                        postAccountId={post.postaccountId}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default PostList;
